/**
 * The submission blob's two branches: `data` holds the user's answers,
 * keyed by authored bind path; `outputs` holds widget-computed values, keyed by
 * the emitting field's id. Both are plain, possibly-nested JSON
 * objects — there is no other shape a bindings snapshot can take.
 */
export interface SubmissionBindings {
  readonly data: Record<string, unknown>;
  readonly outputs: Record<string, unknown>;
}

export const EMPTY_SUBMISSION_BINDINGS: SubmissionBindings = Object.freeze({
  data: Object.freeze({}),
  outputs: Object.freeze({}),
});

/** A single write to a bindings snapshot, addressed by bind path. */
export interface BindingUpdate {
  readonly path: string;
  readonly value: unknown;
}

const OUTPUTS_PREFIX = 'outputs.';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Segments that address the prototype chain rather than own data. Traversing or
 * writing them would leak/pollute the prototype, so they are never valid bind
 * paths — reads resolve to `undefined` and writes are ignored.
 */
function isUnsafeSegment(segment: string): boolean {
  return (
    segment === '__proto__' ||
    segment === 'constructor' ||
    segment === 'prototype'
  );
}

/** Resolution rule: `outputs.`-prefixed paths address `outputs`; everything else addresses `data`. */
function resolveBranch(
  bindings: SubmissionBindings,
  path: string,
): { branch: Record<string, unknown>; segments: string[] } {
  if (path.startsWith(OUTPUTS_PREFIX)) {
    return {
      branch: bindings.outputs,
      segments: path.slice(OUTPUTS_PREFIX.length).split('.'),
    };
  }
  return { branch: bindings.data, segments: path.split('.') };
}

/**
 * Reads a bind path from a bindings snapshot. Resolves to `undefined` for an
 * unset path, or when traversal hits a non-object before consuming every
 * segment.
 */
export function getBindingValue(
  bindings: SubmissionBindings,
  path: string,
): unknown {
  const { branch, segments } = resolveBranch(bindings, path);
  let current: unknown = branch;
  for (const segment of segments) {
    if (!isPlainObject(current) || isUnsafeSegment(segment)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

function setAtPath(
  branch: Record<string, unknown>,
  segments: readonly string[],
  value: unknown,
): Record<string, unknown> {
  const [head, ...rest] = segments;
  if (rest.length === 0) {
    return { ...branch, [head]: value };
  }
  const child = branch[head];
  return {
    ...branch,
    [head]: setAtPath(isPlainObject(child) ? child : {}, rest, value),
  };
}

function deleteAtPath(
  branch: Record<string, unknown>,
  segments: readonly string[],
): Record<string, unknown> {
  const [head, ...rest] = segments;
  if (!(head in branch)) {
    return branch;
  }
  if (rest.length === 0) {
    const remaining = { ...branch };
    delete remaining[head];
    return remaining;
  }
  const child = branch[head];
  if (!isPlainObject(child)) {
    return branch;
  }
  const nextChild = deleteAtPath(child, rest);
  if (Object.keys(nextChild).length === 0) {
    const remaining = { ...branch };
    delete remaining[head];
    return remaining;
  }
  return { ...branch, [head]: nextChild };
}

/**
 * Immutably writes a bind path, returning a new snapshot (structural sharing
 * of untouched branches). `value === undefined` deletes the leaf and prunes
 * any parent object the deletion leaves empty.
 */
export function setBindingValue(
  bindings: SubmissionBindings,
  path: string,
  value: unknown,
): SubmissionBindings {
  const targetsOutputs = path.startsWith(OUTPUTS_PREFIX);
  const segments = targetsOutputs
    ? path.slice(OUTPUTS_PREFIX.length).split('.')
    : path.split('.');
  if (segments.some(isUnsafeSegment)) {
    return bindings;
  }
  const branch = targetsOutputs ? bindings.outputs : bindings.data;
  const nextBranch =
    value === undefined
      ? deleteAtPath(branch, segments)
      : setAtPath(branch, segments, value);
  if (nextBranch === branch) {
    return bindings;
  }

  return targetsOutputs
    ? { data: bindings.data, outputs: nextBranch }
    : { data: nextBranch, outputs: bindings.outputs };
}
